import { NIL } from 'uuid';
import { Address } from './address';

export enum UserType {
  Customer,
  Vendor,
  Employee,
  Owner,
  Accountant,
  SalesRep
}

// item 34 — the three Basix ACCESS roles that drive permissions (Chad, 2026-06-24).
// Values match the backend enum (Admin = 1). Layered on top of CorporateRole, which
// still feeds the commission engine.
export enum AccessRole {
  Admin = 1,
  Accountant,
  Team,
  OutsideSales, // item 272 — external rep self-service seat (a.k.a. "Sales I/O rep", epic 843)
  Executive, // item 594 — operations/sales leader: full ops, no money visibility
  CustomerBuilder, // item 843 — external customer salesperson, bound to one customer; blind proposal builder only
}

export const ACCESS_ROLE_LABELS: Record<AccessRole, string> = {
  [AccessRole.Admin]: 'Admin',
  [AccessRole.Accountant]: 'Accountant',
  [AccessRole.Team]: 'Team',
  [AccessRole.OutsideSales]: 'Outside Sales',
  [AccessRole.Executive]: 'Executive',
  [AccessRole.CustomerBuilder]: 'External', // item 890 — display name; enum value unchanged
};

export const ACCESS_ROLE_DESCRIPTIONS: Record<AccessRole, string> = {
  [AccessRole.Admin]: 'Full access, including the Accountant dashboard, void & archive.',
  [AccessRole.Accountant]: 'Access to everything, but cannot void, archive, or delete.',
  [AccessRole.Team]: 'Projects, Work Orders, and Hand-Offs only.',
  [AccessRole.OutsideSales]: 'External rep — their Book of Business, earnings, commission report + payout request only.',
  [AccessRole.Executive]: 'Operations/sales leader — full Projects, Work Orders, Proposals, Item catalog & CRM; read-only Settings, Hand-Offs & user roster; their own commissions. No financials.',
  [AccessRole.CustomerBuilder]: 'External login bound to a customer. Per-user capabilities pick their portal: the pricing portal (build & submit no-pricing proposals), invoices & billing, or both. Sees nothing else of Basix.',
};

// item 264 — how a user's BASE pay is computed (commissions ride on top regardless).
export enum PayType {
  Hourly = 0,
  Salary = 1,
  // item 1083 — paid via approved IC- invoice docs in Payroll+, never salary/hours.
  // Selecting this IS the IC setup (backend syncs isIndependentContractor from it).
  IndependentContractor = 2,
}

export const PAY_TYPE_LABELS: Record<PayType, string> = {
  [PayType.Hourly]: 'Hourly',
  [PayType.Salary]: 'Salary',
  [PayType.IndependentContractor]: 'Independent contractor (IC)',
};

// item 198 — login-invite lifecycle (Accepted is Auth0-sourced, stubbed until item 197).
export enum InviteStatus {
  Pending = 0,
  Invited = 1,
  Accepted = 2,
  Revoked = 3, // item 1477 — invitation withdrawn (seat deactivated, login blocked, nothing emailed)
}

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  [InviteStatus.Pending]: 'Pending',
  [InviteStatus.Invited]: 'Invited',
  [InviteStatus.Accepted]: 'Accepted',
  [InviteStatus.Revoked]: 'Revoked',
};

// item 84 — corporate role (drives access, item 34). Values match the backend enum.
export enum CorporateRole {
  InsideSales = 1,
  OutsideSales,
  ProjectLead,
  DeploymentTeam,
  DevelopmentTeam,
  Intern,
  Accountant, // item 28 — the accountant identity (sees the Accountant dashboard)
}

export const CORPORATE_ROLE_LABELS: Record<CorporateRole, string> = {
  [CorporateRole.InsideSales]: 'Inside Sales',
  [CorporateRole.OutsideSales]: 'Outside Sales',
  [CorporateRole.ProjectLead]: 'Project Lead',
  [CorporateRole.DeploymentTeam]: 'Deployment Team',
  [CorporateRole.DevelopmentTeam]: 'Development Team',
  [CorporateRole.Intern]: 'Intern',
  [CorporateRole.Accountant]: 'Accountant',
};

// roles that get a money/financial view (others see the project-only PM dashboard, item 102)
export const MONEY_ROLES = [CorporateRole.InsideSales, CorporateRole.OutsideSales];

export interface User {
  id: string;
  name: string;
  address: Address;
  phone: string;
  email: string;
  userType: UserType;
  accessRole?: AccessRole; // item 34
  quickBooksEmployeeId?: string; // item 943 — QB AR export: the (1xx) service-SKU suffix
  quickBooksRepInitials?: string; // item 943 — QB SALESREP initials (outside-sales rep)
  boundCustomerId?: string | null; // item 843 — for CustomerBuilder: the one customer this login is bound to
  corporateRole?: CorporateRole | null;
  commissionRate?: number | null; // item 30 — this rep's own commission % (0 allowed)
  // item 185 — per-reason commission rates (all four on every user; 0 = don't pay that reason)
  ioSalesRate?: number | null;
  salesPORate?: number | null;
  projectLeadRate?: number | null;
  developingPartnerRate?: number | null;
  // item 264 — base pay for payroll
  payType?: PayType;
  // item 1079 — flagged IC seats can submit contractor invoices for approval
  isIndependentContractor?: boolean;
  isOnDraw?: boolean; // item 1489 — commissions paid off the draw report
  hourlyRate?: number | null;
  annualSalary?: number | null;
  isSuperAdmin?: boolean; // item 142 — Pulse Group super-admin (may void/archive/delete)
  canApproveExtraPay?: boolean; // item 499 — super-admin-granted right to approve commissions + timesheets
  isProjectManager?: boolean; // item 1151 — salaried PM role; new-job emails, auto-default when single
  showPortalInvoices?: boolean; // item 868d — External: finalized invoices visible in the portal
  showPortalProposals?: boolean; // item 890 — External: the pricing portal (proposal builder)
  showPortalContracts?: boolean; // item 976 — External: signed agreements + the EEULA loop
  photoUrl?: string | null; // item 318/320 — profile picture (small data URL)
  notes: string;
  createdById: string;
  createdDate: string;
}
export interface UserDto {
  id: string;
  name: string;
  photoUrl?: string | null; // item 339 — avatars on assignees/pickers
  accountingEnteredDate?: string | null; // item 359 — accountant NEW ITEMS queue
  isActive?: boolean; // item 384 — deactivate/reactivate from the grid
  lastModifiedByName: string;
  userType?: UserType;
  accessRole?: AccessRole; // item 34
  quickBooksEmployeeId?: string; // item 943
  quickBooksRepInitials?: string; // item 943
  corporateRole?: CorporateRole | null;
  commissionRate?: number | null; // item 30
  // item 185 — per-reason commission rates
  ioSalesRate?: number | null;
  salesPORate?: number | null;
  projectLeadRate?: number | null;
  developingPartnerRate?: number | null;
  // item 318 — v3 per-reason rates shown on the Users grid
  commissionRates?: { reasonName: string; rate: number }[];
  // item 451 — base pay, for the payroll command center's hourly timesheet math
  payType?: PayType;
  // item 1079 — flagged IC seats can submit contractor invoices for approval
  isIndependentContractor?: boolean;
  isOnDraw?: boolean; // item 1489
  hourlyRate?: number | null;
  // item 198 — invite lifecycle + soft-delete
  inviteStatus?: InviteStatus;
  isArchive?: boolean;
}
export interface UsersDto {
  index: UserDto[];
  length: number;
}
export const getEmptyUserInfo = (): User => ({
  phone: '',
  createdById: '',
  createdDate: '',
  email: '',
  id: NIL,
  notes: '',
  name: '',
  address: {
    street: '', country: '', city: '', state: '', unit: '', zip: '',
  },
  userType: UserType.Employee,
  accessRole: AccessRole.Team, // item 34 — least-privilege default for a new user
});
